import { useState, useRef, useEffect } from 'react';
import { type Table, type VisibilityState } from '@tanstack/react-table';

interface DataTableColumnVisibilityProps {
  table: Table<any>;
  /** The parent's controlled columnVisibility state.
   *
   *  We deliberately read the visibility from this prop instead of from
   *  `column.getIsVisible()`. The dropdown renders as a sibling (above)
   *  of <DataTable>; on a state update the dropdown re-renders FIRST,
   *  before <DataTable>'s useReactTable has had a chance to merge the
   *  new state into `table.options.state`. Reading via the table proxy
   *  therefore gives the previous render's value — the visible "takes
   *  4 clicks" desync. The parent's state is fresh as soon as the parent
   *  re-renders, so we read from there.
   */
  columnVisibility: VisibilityState;
  onColumnVisibilityChange: (next: VisibilityState) => void;
}

export function DataTableColumnVisibility({
  table,
  columnVisibility,
  onColumnVisibilityChange,
}: DataTableColumnVisibilityProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="px-3 py-1 text-xs tracking-wider text-mono-200 hover:text-mono-100 bg-mono-800 rounded transition-colors"
      >
        COLUMNS
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 z-50 min-w-[180px] bg-mono-900 border border-mono-700 rounded-lg shadow-xl p-2 space-y-1">
          {table.getAllLeafColumns().map((column) => {
            // Skip selection column from the visibility toggle list
            if (column.id === 'select') return null;
            // Read from the parent's state directly — TanStack's convention
            // is that an absent key means "visible", so anything not
            // explicitly === false is shown.
            const visible = columnVisibility[column.id] !== false;
            const labelText =
              typeof column.columnDef.header === 'string'
                ? column.columnDef.header
                : column.id;
            return (
              <button
                key={column.id}
                type="button"
                onClick={() =>
                  onColumnVisibilityChange({
                    ...columnVisibility,
                    [column.id]: !visible,
                  })
                }
                className="w-full flex items-center gap-2 px-2 py-1 text-xs text-left text-mono-200 hover:text-mono-100 hover:bg-mono-800/50 rounded cursor-pointer select-none"
              >
                <span
                  aria-hidden
                  className={`w-3.5 h-3.5 rounded border flex items-center justify-center transition-colors ${
                    visible
                      ? 'bg-cyan-500 border-cyan-500'
                      : 'bg-mono-800 border-mono-600'
                  }`}
                >
                  {visible && (
                    <svg viewBox="0 0 12 12" className="w-2.5 h-2.5 text-mono-950" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="2,6.5 5,9 10,3.5" />
                    </svg>
                  )}
                </span>
                <span className="sr-only">
                  {visible ? `Hide column ${labelText}` : `Show column ${labelText}`}
                </span>
                {labelText}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
